import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Loader2, TrendingUp, TrendingDown } from "lucide-react";

/**
 * Pay-change audit feed for one client. Reads
 * `GET /api/integrations/keypay/clients/{cid}/pay-changes`.
 * Rows are written by the nightly KeyPay rollup sync, which diffs each
 * active employee's primary pay rate against the previous snapshot.
 */
export default function KeyPayPayChangesPage() {
  const { clientId } = useParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/integrations/keypay/clients/${clientId}/pay-changes`,
            { params: { limit: 200 } })
      .then((r) => { if (!cancelled) setRows(r.data?.changes || []); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load pay changes"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  return (
    <div className="p-6 max-w-7xl" data-testid="keypay-pay-changes-page">
      <Link to={`/integrations/keypay/clients/${clientId}`}
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3">
        <ArrowLeft className="h-3 w-3" /> Back to dashboard
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-purple-100 text-purple-700">
          <TrendingUp className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">KEYPAY · PAY-CHANGE AUDIT FEED</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Pay-rate changes</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Detected automatically by the nightly KeyPay rollup sync. Each row is one employee whose primary pay rate
            changed between two snapshots.
          </p>
        </div>
      </div>

      {loading ? (
        <Card className="rounded-sm border-zinc-200"><CardContent className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></CardContent></Card>
      ) : rows.length === 0 ? (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-8 text-center text-sm italic text-zinc-500">
            No pay-rate changes have been recorded yet. The audit feed populates after the nightly sync
            (or once you run a manual <span className="font-mono">Sync now</span> from the rollup page).
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-0">
            <div className="grid grid-cols-[1.4fr_120px_110px_110px_110px_100px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
              <span>Employee</span>
              <span className="text-right">From</span>
              <span className="text-right">To</span>
              <span className="text-right">Δ</span>
              <span className="text-right">Δ %</span>
              <span className="text-right">When</span>
            </div>
            {rows.map((r) => (
              <Link key={r.change_id}
                to={`/integrations/keypay/clients/${clientId}/employees/${r.employee_id}`}
                className="grid grid-cols-[1.4fr_120px_110px_110px_110px_100px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-zinc-50">
                <span className="truncate font-semibold">{r.employee_name}</span>
                <span className="font-mono text-right text-xs">${Number(r.from_rate || 0).toFixed(2)}</span>
                <span className="font-mono text-right text-xs">${Number(r.to_rate || 0).toFixed(2)}</span>
                <span className={`font-mono text-right text-xs font-semibold ${r.delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {r.delta >= 0 ? "+" : ""}{Number(r.delta || 0).toFixed(2)}
                </span>
                <Badge variant="outline" className={`rounded-sm text-[10px] justify-self-end ${
                  r.delta >= 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"
                }`}>
                  {r.delta >= 0 ? <TrendingUp className="h-3 w-3 mr-0.5" /> : <TrendingDown className="h-3 w-3 mr-0.5" />}
                  {r.delta_pct !== null ? `${r.delta_pct.toFixed(1)}%` : "—"}
                </Badge>
                <span className="font-mono text-right text-[11px] text-zinc-500">
                  {(r.changed_at || "").slice(0, 10)}
                </span>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
